'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BmiCalculator() {
  const t = useTranslations("tools.bmiCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentUnit, setCurrentUnit] = useState<'imperial' | 'metric'>('imperial');
  const [showResults, setShowResults] = useState(false);

  const [inputs, setInputs] = useState({
    age: 25,
    gender: 'male' as 'male' | 'female',
    feet: 5,
    inches: 10,
    pounds: 160,
    cm: 178,
    kg: 73,
  });

  const [results, setResults] = useState({
    bmi: 0,
    category: '',
    categoryClass: '',
    tip: '',
    healthyWeightMin: 0,
    healthyWeightMax: 0,
    bmiPrime: 0,
    ponderalIndex: 0,
    pointerPosition: 0,
  });

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let heightM: number, weightKg: number;

    if (currentUnit === 'imperial') {
      const totalInches = (inputs.feet * 12) + inputs.inches;
      heightM = totalInches * 0.0254;
      weightKg = inputs.pounds * 0.453592;
    } else {
      heightM = inputs.cm / 100;
      weightKg = inputs.kg;
    }

    if (heightM <= 0 || weightKg <= 0) {
      alert(t('validationError'));
      return;
    }

    const bmi = weightKg / (heightM * heightM);
    const bmiPrime = bmi / 25;
    const ponderalIndex = weightKg / (heightM * heightM * heightM);

    let category: string, categoryClass: string, tip: string;

    if (bmi < 16) {
      category = t('categorySevereThinness');
      categoryClass = 'category-underweight';
      tip = t('tipSevereThinness');
    } else if (bmi < 17) {
      category = t('categoryModerateThinness');
      categoryClass = 'category-underweight';
      tip = t('tipModerateThinness');
    } else if (bmi < 18.5) {
      category = t('categoryMildThinness');
      categoryClass = 'category-underweight';
      tip = t('tipMildThinness');
    } else if (bmi < 25) {
      category = t('categoryNormal');
      categoryClass = 'category-normal';
      tip = t('tipNormal');
    } else if (bmi < 30) {
      category = t('categoryOverweight');
      categoryClass = 'category-overweight';
      tip = t('tipOverweight');
    } else if (bmi < 35) {
      category = t('categoryObeseClass1');
      categoryClass = 'category-obese';
      tip = t('tipObeseClass1');
    } else if (bmi < 40) {
      category = t('categoryObeseClass2');
      categoryClass = 'category-obese';
      tip = t('tipObeseClass2');
    } else {
      category = t('categoryObeseClass3');
      categoryClass = 'category-obese';
      tip = t('tipObeseClass3');
    }

    const minHealthyWeight = 18.5 * heightM * heightM;
    const maxHealthyWeight = 24.9 * heightM * heightM;

    let pointerPosition: number;
    if (bmi < 18.5) {
      pointerPosition = (bmi / 18.5) * 18.5;
    } else if (bmi < 25) {
      pointerPosition = 18.5 + ((bmi - 18.5) / (25 - 18.5)) * 6.5;
    } else if (bmi < 30) {
      pointerPosition = 25 + ((bmi - 25) / (30 - 25)) * 5;
    } else if (bmi < 35) {
      pointerPosition = 30 + ((bmi - 30) / (35 - 30)) * 5;
    } else if (bmi < 40) {
      pointerPosition = 35 + ((bmi - 35) / 5) * 30;
    } else {
      pointerPosition = 95;
    }

    setResults({
      bmi,
      category,
      categoryClass,
      tip,
      healthyWeightMin: currentUnit === 'imperial' ? minHealthyWeight * 2.20462 : minHealthyWeight,
      healthyWeightMax: currentUnit === 'imperial' ? maxHealthyWeight * 2.20462 : maxHealthyWeight,
      bmiPrime,
      ponderalIndex,
      pointerPosition: Math.min(pointerPosition, 95),
    });

    setShowResults(true);

    setTimeout(() => {
      const resultsElement = document.getElementById('results-section');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 20px rgba(37, 99, 235, 0.1)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1000px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorContentStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginTop: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '2rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '2rem' }
  );

  const calculatorFormWrapperStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const unitTabsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #e2e8f0',
  };

  const unitTabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '0.75rem 1.5rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.95rem',
    color: isActive ? '#2563eb' : '#64748b',
    borderBottom: isActive ? '3px solid #2563eb' : '3px solid transparent',
    transition: 'all 0.3s ease',
    fontWeight: 500,
  });

  const calculatorFormStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  };

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#334155',
    fontSize: '0.95rem',
  };

  const inputStyle: React.CSSProperties = {
    padding: '0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
  };

  const heightInputsStyle = getResponsiveStyle(
    { display: 'grid', gap: '0.75rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const inputWithUnitStyle: React.CSSProperties = {
    position: 'relative',
  };

  const inputUnitStyle: React.CSSProperties = {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontSize: '0.9rem',
    pointerEvents: 'none',
  };

  const genderOptionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1rem',
  };

  const genderOptionStyle: React.CSSProperties = {
    flex: 1,
    position: 'relative',
  };

  const genderLabelStyle = (isChecked: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.75rem',
    border: `2px solid ${isChecked ? '#2563eb' : '#e2e8f0'}`,
    borderRadius: '0.5rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontWeight: 500,
    background: isChecked ? '#2563eb' : 'transparent',
    color: isChecked ? 'white' : '#334155',
  });

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '0.5rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      display: showResults ? 'block' : 'none',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const bmiValueDisplayStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '1.5rem',
  };

  const bmiNumberStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' },
    { fontSize: '3rem' }
  );

  const bmiCategoryStyle = (categoryClass: string): React.CSSProperties => {
    const categoryColors: { [key: string]: { bg: string; color: string } } = {
      'category-underweight': { bg: '#fef3c7', color: '#92400e' },
      'category-normal': { bg: '#d1fae5', color: '#065f46' },
      'category-overweight': { bg: '#fed7aa', color: '#9a3412' },
      'category-obese': { bg: '#fecaca', color: '#991b1b' },
    };

    const colors = categoryColors[categoryClass] || categoryColors['category-normal'];

    return {
      fontSize: '1.25rem',
      fontWeight: 600,
      padding: '0.5rem 1rem',
      borderRadius: '0.5rem',
      display: 'inline-block',
      background: colors.bg,
      color: colors.color,
    };
  };

  const bmiScaleStyle: React.CSSProperties = {
    position: 'relative',
    height: '40px',
    background: 'linear-gradient(to right, #3b82f6 0%, #10b981 18.5%, #10b981 25%, #fbbf24 25%, #fbbf24 30%, #f97316 30%, #f97316 35%, #ef4444 35%)',
    borderRadius: '20px',
    margin: '1.5rem 0',
  };

  const bmiPointerStyle: React.CSSProperties = {
    position: 'absolute',
    top: '-10px',
    left: `${results.pointerPosition}%`,
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderTop: '15px solid #1e293b',
    transform: 'translateX(-50%)',
    transition: 'left 0.5s ease',
  };

  const bmiScaleLabelsStyle = getResponsiveStyle(
    { display: 'flex', justifyContent: 'space-between', color: '#64748b', marginTop: '0.5rem' },
    { fontSize: '0.75rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.85rem' }
  );

  const resultDetailsStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginTop: '1.5rem',
  };

  const resultItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #2563eb',
  };

  const resultLabelStyle: React.CSSProperties = {
    color: '#64748b',
    fontWeight: 500,
  };

  const resultValueStyle: React.CSSProperties = {
    color: colors.textPrimary,
    fontWeight: 700,
  };

  const healthTipStyle: React.CSSProperties = {
    marginTop: '1.5rem',
    padding: '1rem',
    background: '#eff6ff',
    borderLeft: '4px solid #2563eb',
    borderRadius: '0.5rem',
  };

  const healthTipTitleStyle: React.CSSProperties = {
    color: '#1e40af',
    marginBottom: '0.5rem',
    fontSize: '1rem',
    fontWeight: 600,
  };

  const healthTipTextStyle: React.CSSProperties = {
    color: '#1e40af',
    fontSize: '0.9rem',
    lineHeight: 1.5,
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle: React.CSSProperties = {
    marginLeft: '1.5rem',
    marginTop: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0.75rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const referenceStyle: React.CSSProperties = {
    color: colors.textSecondary,
    fontSize: '0.9rem',
    lineHeight: 1.6,
    marginBottom: '0.5rem',
  };

  const linkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>🧮 {t('toolTitle')}</h2>
          </div>

          <div style={calculatorContentStyle}>
            <div style={calculatorFormWrapperStyle}>
              <div style={unitTabsStyle}>
                <button
                  type="button"
                  style={unitTabStyle(currentUnit === 'imperial')}
                  onClick={() => setCurrentUnit('imperial')}
                  onMouseEnter={(e) => {
                    if (currentUnit !== 'imperial') {
                      e.currentTarget.style.color = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentUnit !== 'imperial') {
                      e.currentTarget.style.color = '#64748b';
                    }
                  }}
                >
                  {t('usUnits')}
                </button>
                <button
                  type="button"
                  style={unitTabStyle(currentUnit === 'metric')}
                  onClick={() => setCurrentUnit('metric')}
                  onMouseEnter={(e) => {
                    if (currentUnit !== 'metric') {
                      e.currentTarget.style.color = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentUnit !== 'metric') {
                      e.currentTarget.style.color = '#64748b';
                    }
                  }}
                >
                  {t('metricUnits')}
                </button>
              </div>

              <form style={calculatorFormStyle} onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="age">{t('ageLabel')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="age"
                    min="2"
                    max="120"
                    value={inputs.age}
                    onChange={(e) => setInputs({ ...inputs, age: parseInt(e.target.value) || 0 })}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('genderLabel')}</label>
                  <div style={genderOptionsStyle}>
                    <div style={genderOptionStyle}>
                      <input
                        type="radio"
                        id="male"
                        name="gender"
                        value="male"
                        checked={inputs.gender === 'male'}
                        onChange={(e) => setInputs({ ...inputs, gender: e.target.value as 'male' | 'female' })}
                        style={{ position: 'absolute', opacity: 0 }}
                      />
                      <label htmlFor="male" style={genderLabelStyle(inputs.gender === 'male')}>
                        {t('male')}
                      </label>
                    </div>
                    <div style={genderOptionStyle}>
                      <input
                        type="radio"
                        id="female"
                        name="gender"
                        value="female"
                        checked={inputs.gender === 'female'}
                        onChange={(e) => setInputs({ ...inputs, gender: e.target.value as 'male' | 'female' })}
                        style={{ position: 'absolute', opacity: 0 }}
                      />
                      <label htmlFor="female" style={genderLabelStyle(inputs.gender === 'female')}>
                        {t('female')}
                      </label>
                    </div>
                  </div>
                </div>

                {currentUnit === 'imperial' ? (
                  <div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t('heightLabel')}</label>
                      <div style={heightInputsStyle}>
                        <div style={inputWithUnitStyle}>
                          <input
                            style={{ ...inputStyle, width: '100%', paddingRight: '3rem' }}
                            type="number"
                            id="feet"
                            min="0"
                            max="8"
                            value={inputs.feet}
                            onChange={(e) => setInputs({ ...inputs, feet: parseInt(e.target.value) || 0 })}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#2563eb';
                              e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e2e8f0';
                              e.target.style.boxShadow = 'none';
                            }}
                            required
                          />
                          <span style={inputUnitStyle}>{t('feet')}</span>
                        </div>
                        <div style={inputWithUnitStyle}>
                          <input
                            style={{ ...inputStyle, width: '100%', paddingRight: '3rem' }}
                            type="number"
                            id="inches"
                            min="0"
                            max="11"
                            value={inputs.inches}
                            onChange={(e) => setInputs({ ...inputs, inches: parseInt(e.target.value) || 0 })}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#2563eb';
                              e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e2e8f0';
                              e.target.style.boxShadow = 'none';
                            }}
                            required
                          />
                          <span style={inputUnitStyle}>{t('inches')}</span>
                        </div>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="pounds">{t('weightLabel')}</label>
                      <div style={inputWithUnitStyle}>
                        <input
                          style={{ ...inputStyle, width: '100%', paddingRight: '3rem' }}
                          type="number"
                          id="pounds"
                          min="1"
                          step="0.1"
                          value={inputs.pounds}
                          onChange={(e) => setInputs({ ...inputs, pounds: parseFloat(e.target.value) || 0 })}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#2563eb';
                            e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.boxShadow = 'none';
                          }}
                          required
                        />
                        <span style={inputUnitStyle}>{t('lbs')}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="cm">{t('heightLabel')}</label>
                      <div style={inputWithUnitStyle}>
                        <input
                          style={{ ...inputStyle, width: '100%', paddingRight: '3rem' }}
                          type="number"
                          id="cm"
                          min="1"
                          step="0.1"
                          value={inputs.cm}
                          onChange={(e) => setInputs({ ...inputs, cm: parseFloat(e.target.value) || 0 })}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#2563eb';
                            e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.boxShadow = 'none';
                          }}
                          required
                        />
                        <span style={inputUnitStyle}>{t('cm')}</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="kg">{t('weightLabel')}</label>
                      <div style={inputWithUnitStyle}>
                        <input
                          style={{ ...inputStyle, width: '100%', paddingRight: '3rem' }}
                          type="number"
                          id="kg"
                          min="1"
                          step="0.1"
                          value={inputs.kg}
                          onChange={(e) => setInputs({ ...inputs, kg: parseFloat(e.target.value) || 0 })}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#2563eb';
                            e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.boxShadow = 'none';
                          }}
                          required
                        />
                        <span style={inputUnitStyle}>{t('kg')}</span>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  style={calculateBtnStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t('calculateButton')}
                </button>
              </form>
            </div>

            <div id="results-section" style={resultsStyle}>
              <h3 style={{ ...h2Style, textAlign: 'center' }}>{t('yourResults')}</h3>

              <div style={bmiValueDisplayStyle}>
                <div style={bmiNumberStyle}>{results.bmi.toFixed(1)}</div>
                <div style={bmiCategoryStyle(results.categoryClass)}>{results.category}</div>
              </div>

              <div style={{ margin: '2rem 0' }}>
                <div style={bmiScaleStyle}>
                  <div style={bmiPointerStyle}></div>
                </div>
                <div style={bmiScaleLabelsStyle}>
                  <span>{t('underweight')}<br />&lt;18.5</span>
                  <span>{t('normal')}<br />18.5-24.9</span>
                  <span>{t('overweight')}<br />25-29.9</span>
                  <span>{t('obese')}<br />≥30</span>
                </div>
              </div>

              <div style={resultDetailsStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('healthyBmiRange')}</span>
                  <span style={resultValueStyle}>18.5 - 24.9 kg/m²</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('healthyWeightRange')}</span>
                  <span style={resultValueStyle}>
                    {results.healthyWeightMin.toFixed(1)} - {results.healthyWeightMax.toFixed(1)} {currentUnit === 'imperial' ? t('lbs') : t('kg')}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('bmiPrime')}</span>
                  <span style={resultValueStyle}>{results.bmiPrime.toFixed(2)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('ponderalIndex')}</span>
                  <span style={resultValueStyle}>{results.ponderalIndex.toFixed(1)} kg/m³</span>
                </div>
              </div>

              <div style={healthTipStyle}>
                <h4 style={healthTipTitleStyle}>💡 {t('healthInsight')}</h4>
                <p style={healthTipTextStyle}>{results.tip}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insightBmiAsScreeningTitle')}</h3>
        <p style={paragraphStyle}>{t('insightBmiAsScreening')}</p>

        <h3 style={h3Style}>{t('insightHealthRisksTitle')}</h3>
        <p style={paragraphStyle}>{t('insightHealthRisks')}</p>

        <h3 style={h3Style}>{t('insightCurrentGuidelinesTitle')}</h3>
        <p style={paragraphStyle}>{t('insightCurrentGuidelines')}</p>

        <h3 style={h3Style}>{t('insightLimitationsTitle')}</h3>
        <p style={paragraphStyle}>{t('insightLimitations')}</p>

        <h3 style={h3Style}>{t('insightBestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightBestPractice1')}</li>
          <li style={listItemStyle}>{t('insightBestPractice2')}</li>
          <li style={listItemStyle}>{t('insightBestPractice3')}</li>
          <li style={listItemStyle}>{t('insightBestPractice4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailFormulaTitle')}</h3>
        <p style={paragraphStyle}>{t('detailFormula')}</p>

        <h3 style={h3Style}>{t('detailCategoriesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('detailCategory1')}</li>
          <li style={listItemStyle}>{t('detailCategory2')}</li>
          <li style={listItemStyle}>{t('detailCategory3')}</li>
          <li style={listItemStyle}>{t('detailCategory4')}</li>
          <li style={listItemStyle}>{t('detailCategory5')}</li>
          <li style={listItemStyle}>{t('detailCategory6')}</li>
          <li style={listItemStyle}>{t('detailCategory7')}</li>
        </ul>

        <h3 style={h3Style}>{t('detailAdditionalMetricsTitle')}</h3>
        <p style={paragraphStyle}>{t('detailAdditionalMetrics')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion1')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer1')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion2')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer2')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion3')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer3')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion4')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer4')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion5')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer5')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion6')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer6')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={referenceStyle}>
          1. Centers for Disease Control and Prevention (CDC). <a href="https://www.cdc.gov/bmi/about/index.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('reference1')}</a>
        </div>
        <div style={referenceStyle}>
          2. Cleveland Clinic. <a href="https://my.clevelandclinic.org/health/articles/9464-body-mass-index-bmi" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('reference2')}</a>
        </div>
        <div style={referenceStyle}>
          3. American Cancer Society. <a href="https://www.cancer.org/cancer/risk-prevention/diet-physical-activity/body-weight-and-cancer-risk/adult-bmi.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('reference3')}</a>
        </div>
        <div style={referenceStyle}>
          4. National Heart, Lung, and Blood Institute (NHLBI). <a href="https://www.nhlbi.nih.gov/health/educational/lose_wt/BMI/bmicalc.htm" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('reference4')}</a>
        </div>
        <div style={referenceStyle}>
          5. American Heart Association. <a href="https://www.heart.org/en/healthy-living/healthy-eating/losing-weight/bmi-in-adults" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('reference5')}</a>
        </div>
      </section>
    </div>
  );
}